import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import {
  createSignupOption, updateSignupOption, deleteSignupOption, splitSignupOption,
  createSignupSlot, deleteSignupSlot, getSignupDayBlocks,
} from "../../lib/api";
import { prettyDate } from "./signupHelpers";

const blankBlock = {
  type: "block", title: "", description: "",
  slot_id: "", date: "", lead_staff_id: "", support_staff_ids: [],
  location: "", capacity: "", notes: "",
};
const blankTrip = {
  type: "trip", title: "", description: "",
  dates: [], covered_blocks: [], lead_staff_id: "", support_staff_ids: [],
  location: "", capacity: "", notes: "",
};

export default function OptionEditorDialog({ open, onOpenChange, editing, defaultType = "block", defaults = {}, slots, staff, weekDates, onSaved, allowTypeSwitch = true }) {
  const [form, setForm] = useState(blankBlock);
  const [saving, setSaving] = useState(false);
  const [newSlotName, setNewSlotName] = useState("");
  const [newTripDate, setNewTripDate] = useState("");
  const [dayBlocks, setDayBlocks] = useState([]);

  // Load the master-schedule blocks so a trip can cover specific ones.
  useEffect(() => {
    if (!open || form.type !== "trip") return;
    getSignupDayBlocks((form.dates && form.dates[0]) || "")
      .then((res) => setDayBlocks(res.data.blocks || []))
      .catch(() => setDayBlocks([]));
  }, [open, form.type, form.dates]);

  // New/legacy trips default to covering all blocks (full-day) until staff narrow it.
  useEffect(() => {
    if (!open || form.type !== "trip") return;
    if (dayBlocks.length && (!form.covered_blocks || form.covered_blocks.length === 0)) {
      setForm((p) => ({ ...p, covered_blocks: dayBlocks.map((b) => b.key) }));
    }
  }, [open, form.type, dayBlocks]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggleBlock = (key) => {
    const cur = form.covered_blocks || [];
    update({ covered_blocks: cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key] });
  };

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...editing,
        capacity: editing.capacity != null ? String(editing.capacity) : "",
        support_staff_ids: editing.support_staff_ids || [],
        dates: editing.dates || [],
      });
    } else if (defaultType === "trip") {
      setForm({ ...blankTrip, dates: defaults.date ? [defaults.date] : [] });
    } else {
      setForm({ ...blankBlock, slot_id: defaults.slot_id || "", date: defaults.date || weekDates?.[0] || "" });
    }
  }, [open, editing, defaultType, weekDates, defaults.slot_id, defaults.date]);

  const update = (patch) => setForm((p) => ({ ...p, ...patch }));

  const toggleSupport = (id) => {
    const cur = form.support_staff_ids || [];
    update({ support_staff_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  const addTripDate = () => {
    if (!newTripDate) return;
    if (!form.dates.includes(newTripDate)) update({ dates: [...form.dates, newTripDate].sort() });
    setNewTripDate("");
  };

  const removeTripDate = (d) => update({ dates: form.dates.filter((x) => x !== d) });

  const handleAddSlot = async () => {
    if (!newSlotName.trim()) return;
    try {
      const res = await createSignupSlot({ name: newSlotName.trim(), sort_order: (slots?.length || 0) + 1 });
      toast.success(`Slot "${res.data.name}" added`);
      update({ slot_id: res.data.id });
      setNewSlotName("");
      onSaved && onSaved({ refreshSlots: true, keepOpen: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add slot");
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.type === "block" && (!form.slot_id || !form.date)) {
      toast.error("Pick a slot and a date"); return;
    }
    if (form.type === "trip" && (!form.dates || form.dates.length === 0)) {
      toast.error("Add at least one trip date"); return;
    }
    if (form.type === "trip" && dayBlocks.length && (!form.covered_blocks || form.covered_blocks.length === 0)) {
      toast.error("Select at least one block the trip covers"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        capacity: form.capacity === "" || form.capacity === null ? null : Number(form.capacity),
      };
      if (editing) {
        await updateSignupOption(editing.id, payload);
        toast.success("Updated");
      } else if (payload.type === "trip" && (payload.dates || []).length > 1) {
        // Spawn one independent single-date trip per date so staff can differ per date.
        const dates = [...payload.dates].sort();
        for (const d of dates) {
          await createSignupOption({ ...payload, dates: [d] });
        }
        toast.success(`Created ${dates.length} trips — one per date`);
      } else {
        await createSignupOption(payload);
        toast.success("Added");
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this option and all of its student picks?")) return;
    try {
      await deleteSignupOption(editing.id);
      toast.success("Deleted");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const handleSplit = async () => {
    if (!editing) return;
    if (!window.confirm("Split this trip into a separate trip for each date? Sign-ups will be moved to the matching dates, and you can then set staff per date. This can't be undone.")) return;
    setSaving(true);
    try {
      const res = await splitSignupOption(editing.id);
      toast.success(`Split into ${res.data.created} dated trips`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to split");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Option" : `Add ${form.type === "trip" ? "Trip / Event" : "Block Option"}`}</DialogTitle>
          <DialogDescription>
            {form.type === "block"
              ? "Add an activity to a specific slot and date. Students will pick one option per slot."
              : "One-off trip or event with multiple dates. Students respond Yes / No / Maybe."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!editing && allowTypeSwitch && (
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={form.type === "block" ? "default" : "outline"} onClick={() => setForm({ ...blankBlock, date: weekDates?.[0] || "" })}>Block Option</Button>
              <Button type="button" size="sm" variant={form.type === "trip" ? "default" : "outline"} onClick={() => setForm({ ...blankTrip })}>Trip / Event</Button>
            </div>
          )}

          <div className="space-y-1">
            <Label>Title *</Label>
            <Input value={form.title} onChange={(e) => update({ title: e.target.value })} placeholder="e.g., Photography Walk" data-testid="opt-title" />
          </div>

          <div className="space-y-1">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => update({ description: e.target.value })} placeholder="What will the kids do?" />
          </div>

          {form.type === "block" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Slot *</Label>
                <Select value={form.slot_id || "none"} onValueChange={(v) => update({ slot_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="opt-slot"><SelectValue placeholder="Pick a slot" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Pick a slot —</SelectItem>
                    {slots.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1 pt-1">
                  <Input
                    placeholder="New slot name (e.g., Friday PM)"
                    value={newSlotName}
                    onChange={(e) => setNewSlotName(e.target.value)}
                    className="h-7 text-xs"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={handleAddSlot} className="h-7 px-2 text-xs">+ Add Slot</Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Date *</Label>
                <Select value={form.date || "none"} onValueChange={(v) => update({ date: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="opt-date"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Pick a date —</SelectItem>
                    {(weekDates || []).map((d) => <SelectItem key={d} value={d}>{prettyDate(d)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <Label>Dates *</Label>
              <div className="flex gap-2">
                <Input type="date" value={newTripDate} onChange={(e) => setNewTripDate(e.target.value)} className="max-w-xs" />
                <Button type="button" size="sm" variant="outline" onClick={addTripDate}>+ Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 pt-1">
                {form.dates.map((d) => (
                  <Badge key={d} variant="secondary" className="cursor-pointer" onClick={() => removeTripDate(d)}>
                    {prettyDate(d)} ✕
                  </Badge>
                ))}
              </div>
              {!editing && form.dates.length > 1 && (
                <p className="text-xs text-muted-foreground pt-1">
                  Each date will become its own trip you can staff separately.
                </p>
              )}
              {editing && (form.dates || []).length > 1 && (
                <div className="pt-2">
                  <Button type="button" size="sm" variant="outline" onClick={handleSplit} disabled={saving} data-testid="opt-split">
                    Split into separate dates
                  </Button>
                  <p className="text-xs text-muted-foreground pt-1">
                    Turns this multi-date trip into one trip per date so you can set a different lead/staff for each. Sign-ups move to the matching dates.
                  </p>
                </div>
              )}

              <div className="space-y-1 pt-2">
                <Label>Blocks covered *</Label>
                <p className="text-xs text-muted-foreground">
                  Pick which blocks this trip covers. Students are off-site (accounted for as “Trip”) only during these blocks, and can still sign up for the others.
                </p>
                <div className="flex flex-wrap gap-1 pt-1" data-testid="trip-blocks">
                  {dayBlocks.length === 0 && <span className="text-xs text-muted-foreground">No blocks configured.</span>}
                  {dayBlocks.map((b) => {
                    const on = (form.covered_blocks || []).includes(b.key);
                    return (
                      <Badge
                        key={b.key}
                        variant={on ? "default" : "outline"}
                        className={`cursor-pointer ${on ? "bg-[#00305E]" : ""}`}
                        onClick={() => toggleBlock(b.key)}
                        data-testid={`trip-block-${b.key}`}
                      >
                        {b.name}{b.start_time ? ` (${b.start_time}–${b.end_time})` : ""}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Lead Staff</Label>
              <Select value={form.lead_staff_id || "none"} onValueChange={(v) => update({ lead_staff_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => update({ location: e.target.value })} placeholder="e.g., Gym, Art room" />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Support Staff (multi-select)</Label>
            <div className="flex flex-wrap gap-1 border rounded p-2 bg-gray-50 max-h-32 overflow-y-auto">
              {staff.map((s) => (
                <Badge key={s.id} variant={form.support_staff_ids.includes(s.id) ? "default" : "outline"} className="cursor-pointer" onClick={() => toggleSupport(s.id)}>
                  {s.name}
                </Badge>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Capacity (soft cap)</Label>
              <Input type="number" min="0" value={form.capacity} onChange={(e) => update({ capacity: e.target.value })} placeholder="Leave blank for unlimited" />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => update({ notes: e.target.value })} placeholder="Anything else" />
            </div>
          </div>
        </div>

        <div className="flex justify-between gap-2 pt-3 border-t">
          {editing ? (
            <Button variant="ghost" className="text-red-600" onClick={handleDelete}>Delete</Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="opt-save">
              {saving ? "Saving..." : editing ? "Save" : "Add"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SlotManager({ open, onOpenChange, slots, onChanged }) {
  const [newName, setNewName] = useState("");
  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      await createSignupSlot({ name: newName.trim(), sort_order: (slots?.length || 0) + 1 });
      setNewName("");
      onChanged && onChanged();
      toast.success("Slot added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this slot and all its options & picks?")) return;
    try {
      await deleteSignupSlot(id);
      onChanged && onChanged();
      toast.success("Slot deleted");
    } catch (e) {
      toast.error("Failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Manage Sign-Up Slots</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g., Block 5" />
            <Button onClick={handleAdd}>+ Add</Button>
          </div>
          <div className="border rounded">
            {(slots || []).length === 0 && <div className="p-3 text-sm text-muted-foreground">No slots configured yet.</div>}
            {(slots || []).map((s) => (
              <div key={s.id} className="flex items-center justify-between p-2 border-b last:border-b-0">
                <span className="text-sm font-medium">{s.name}</span>
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(s.id)}>Delete</Button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
